import { useEffect, useMemo, useState } from "react";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { useGetAllStatesQuery } from "../../data/states/statesApi";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";
import {
    useCreateCustomerPricingMutation,
    useDeleteCustomerPricingByIdMutation,
    useLazyExportCustomerPricingDataQuery,
    useLazySearchCustomerPricingQuery,
    useUpdateCustomerPricingMutation,
} from "./customerPricingApi";
import { skipToken } from "@reduxjs/toolkit/query";
import PricingTableHead from "../../../components/common/PricingTableHead";
import { useGetAllTenantsQuery } from "../../customer/tenantApi";
import { useGetAllCountyByStateIdQuery } from "../../data/counties/countiesApi";
import { useGetMetroAreaByStateIdQuery } from "../../data/metroAreas/metroAreasApi";
import NumberFormat from "react-number-format";
import Pagination from "../../../components/common/pagination/Pagination";
import { useGetAllCustomerByTenantQuery } from "../../customer/customerApi";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import { showToast } from "../../../helpers/utils/Utils";
import ModalButton from "../../../components/common/form/ModalButton";
import IconButton from "../../../components/common/form/IconButton";
import { FaTrash, FaUndo } from "react-icons/fa";
import Preloader from "../../../components/common/Preloader";
import { FaFileExport } from "react-icons/fa6";
import { useGetBranchesByCustomerIdsQuery } from "../../customer/branchApi";
import { useUtilDataQuery } from "../../profile/profileApi";


const CustomerPricing = () => {

    useEffect(() => {
        document.title = "Customer Pricing";
    });

    const { data: utilData } = useUtilDataQuery("");
    const { data: enums } = useGetEnumsQuery("");
    const [pricingQuery, setPricingQuery] = useState({});
    const [customerPricingsQueryApi, customerPricingData] = useLazySearchCustomerPricingQuery();
    const defaultPageSize = useMemo(() => 15, []);
    const [description, setDescription] = useState("");
    const [productType, setProductType] = useState("");
    const [id, setId] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [sortOrder, setSortOrder] = useState("ASCENDING");
    const [sort, setSort] = useState("creationDate");
    const [state, setState] = useState("");
    const [tenant, setTenant] = useState(utilData?.customerLevel === 'SYS' ? "" : utilData?.tenant);
    const [customer, setCustomer] = useState("");
    const [county, setCounty] = useState("");
    const [metroArea, setMetroArea] = useState("");
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [descriptionAlert, setDescriptionAlert] = useState(false);
    const [systemGenerated, setSystemGenerated] = useState(false);
    const [alert, setAlert] = useState(false);
    const [isApplySearch, setIsApplySearch] = useState(false);
    const [showEditIcon, setShowEditIcon] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const statesQueryApi = useGetAllStatesQuery("");
    const tenantsQueryApi = useGetAllTenantsQuery("");
    const countyByStateId = useGetAllCountyByStateIdQuery(state || skipToken);
    const metroAreaByStateId = useGetMetroAreaByStateIdQuery(state || skipToken);
    const customerTenantResponseApi = useGetAllCustomerByTenantQuery(
        tenant ? { tenantName: tenant } : skipToken
    ); const branchData = useGetBranchesByCustomerIdsQuery(customer)
    const [deleteCustomerPricing, { isLoading }] = useDeleteCustomerPricingByIdMutation();
    const [submitCustomerPricing] = useCreateCustomerPricingMutation();
    const [updateCustomerPricing] = useUpdateCustomerPricingMutation();
    const [branch, setBranch] = useState("");
    const [displayPrice, setDisplayPrice] = useState("");
    const [displayPricePerParcel, setDisplayPricePerParcel] = useState("");
    const [price, setPrice] = useState("");
    const [pricePerParcel, setPricePerParcel] = useState("");
    const [isExport, setIsExport] = useState(false);
    const [exportCustomerPricingApi] = useLazyExportCustomerPricingDataQuery();
    const [thead, setThead] = useState<Array<{ content: string; sortColumn: string }>>([]);

    function showEditRow(obj: any) {
        setPageNumber(0);
        setId(obj.id);
        setDescription(obj.description);
        setProductType(obj.productType);
        setTenant(obj.tenant ? obj.tenant : "");
        setSystemGenerated(obj.systemGenerated);
        setCustomer(obj.customerId ? obj.customerId : "");
        setBranch(obj.branchId ? obj.branchId : "");
        setState(obj.stateId ? obj.stateId : "");
        setCounty(obj.countyId ? obj.countyId : "");
        setMetroArea(obj.metroAreaId ? obj.metroAreaId : "");
        setDisplayPrice(obj.displayPrice);
        setDisplayPricePerParcel(obj.displayPricePerParcel);
        setPrice(obj.price);
        setPricePerParcel(obj.pricePerParcel);
    }

    function getQuery() {
        return {
            pageNumber: pageNumber,
            pageSize: pageSize,
            sort: sort,
            sortOrder: sortOrder,
            stateId: state ? state : null,
            countyId: county ? county : null,
            metroAreaId: metroArea ? metroArea : null,
            productType: productType,
            tenant: tenant,
            customerId: customer,
            branchId: branch

        }
    }

    useEffect(() => {
        if (pricingQuery) {
            customerPricingsQueryApi(pricingQuery);
        }
    }, [pricingQuery]);

    useEffect(() => {
        if (pageNumber >= 0) {
            setPricingQuery({ pageNumber: pageNumber })
        }
    }, [pageNumber]);

    useEffect(() => {
        if (pageSize) {
            setPricingQuery({ pageNumber: pageNumber, pageSize: pageSize })
        }
    }, [pageSize]);

    useEffect(() => {
        if (isApplySearch) {
            let query = getQuery();
            customerPricingsQueryApi(query);
            setIsApplySearch(false);
        }
    }, [isApplySearch]);

    useEffect(() => {
        if (sort) {
            let query = getQuery();
            customerPricingsQueryApi(query);
            setIsApplySearch(false);
        }
    }, [sort, sortOrder]);

    useEffect(() => {
        if (!utilData) return;

        const isSysUser = utilData?.customerLevel?.trim().toUpperCase() === "SYS";

        const commonColumns = [
            { content: "Description", sortColumn: "description" },
            { content: "Product", sortColumn: "productType" },
            ...(isSysUser ? [{ content: "Tenant", sortColumn: "tenant" }] : []),
            { content: "Customer", sortColumn: "customerName" },
            { content: "Branch", sortColumn: "branchName" },
            { content: "State", sortColumn: "stateName" },
            { content: "MSA", sortColumn: "metroAreaName" },
            { content: "County", sortColumn: "countyName" },
            { content: "Display Price", sortColumn: "displayPrice" },
            { content: "Display Parcel", sortColumn: "displayPricePerParcel" },
            { content: "Price", sortColumn: "price" },
            { content: "Parcel", sortColumn: "pricePerParcel" },
        ];

        const actionColumn = hasPermission(["PRIC-D"])
            ? { content: "Action", sortColumn: "" }
            : { content: "", sortColumn: "" };

        setThead([...commonColumns, actionColumn]);
    }, [utilData]);

    const input = {
        id: id,
        description: description,
        customerId: customer === '' ? null : customer,
        branchId: branch === '' ? null : branch,
        county: county === '' ? null : { id: county },
        state: state === '' ? null : { id: state },
        tenant: tenant === '' ? null : tenant,
        metroArea: metroArea === '' ? null : { id: metroArea },
        productType: productType,
        displayPrice: displayPrice === '' ? 0.0 : displayPrice,
        displayPricePerParcel: displayPricePerParcel === '' ? 0.0 : displayPricePerParcel,
        price: price === '' ? 0.0 : price,
        pricePerParcel: pricePerParcel === '' ? 0.0 : pricePerParcel,
    }

    function handleSubmit(e: any) {
        e.preventDefault();
        if (input.productType === '') {
            showToast("Product Type must be selected!", "error");
            setAlert(true);
        } else if (input.county && input.metroArea) {
            return showToast("Both County and Metro Area cannot be selected");
        } else {
            setIsSubmit(true);
        }
    }

    function resetInput() {
        setId(null);
        setDescription("");
        setProductType("");
        setTenant(utilData?.customerLevel === 'SYS' ? "" : utilData?.tenant);
        setCustomer("");
        setBranch("");
        setState("");
        setCounty("");
        setMetroArea("");
        setDisplayPrice("");
        setDisplayPricePerParcel("");
        setPrice("");
        setPricePerParcel("");
        customerPricingsQueryApi(pricingQuery)
    }

    useEffect(() => {
        if (isSubmit) { 
            submitCustomerPricing({ requestBody: input, id: id });
            setIsSubmit(false);
            setShowEditIcon(false);
            resetInput();
        }

    }, [isSubmit]);

    const handleDelete = async (customerId: any) => {
        if (customerId) {
            await deleteCustomerPricing(customerId).unwrap();
        }
    };

    const exportCustomerPricing = async () => {
        const query = getQuery();
        await exportCustomerPricingApi(query);
        setIsExport(false);
    };


    return (
        <>
            {customerPricingData?.data === undefined
                ? <Preloader />
                :
                <>
                    <div className="gap-1 text-text-0 flex justify-end absolute right-[10px] top-0">
                        {hasPermission(["PRIC-R"]) &&
                            <IconButton icon={<FaFileExport />}
                                hasBackground={true}
                                title={"Export Pricing"}
                                onClick={(e: any) => {
                                    if (!isExport) {
                                        e.preventDefault();
                                        setIsExport(true);
                                        exportCustomerPricing();
                                    }
                                }}
                                className={`px-3 py-2 rounded-xl ${isExport ? "bg-surface-4 export" : " export"}`}
                            />}
                        <IconButton icon={<FaUndo size={14} />}
                            hasBackground={true}
                            title={"Reset Filter"}
                            onClick={() => {
                                setShowEditIcon(false);
                                setSystemGenerated(false);
                                resetInput();
                            }}
                            className={'px-3 py-2 rounded-xl '}
                        />
                    </div>
                    <div className={'overflow-auto'}>
                        <table className="p-3 w-full">

                            {!customerPricingData?.data && <thead>
                                <tr>
                                    {thead?.map((header) => {
                                        return (
                                            <th scope="col" className={'bg-highlight-0 text-text-0'}><a
                                                className="toggle-data-header toggle-data cursor-pointer">{header.content}<i
                                                    className="fas fa-sort" /></a></th>
                                        )
                                    })
                                    }
                                </tr>
                            </thead>
                            }

                            {customerPricingData.data &&
                                <PricingTableHead headers={thead} data={customerPricingData?.data}
                                    setSort={setSort}
                                    sortOrder={sortOrder} setSortOrder={setSortOrder}
                                />}
                            <tbody>
                                {hasPermission(["PRIC-C"]) && <tr className={"bg-surface-7"}>
                                    <td>
                                        <textarea
                                            className={descriptionAlert ? "alertClass form-control table_data1" : "form-control table_data1 border border-surface-5"}
                                            placeholder="Enter Description..."
                                            value={description}
                                            onChange={(e: any) => {
                                                setDescription(e.target.value);
                                                setDescriptionAlert(false);
                                            }}
                                            disabled={systemGenerated}
                                        />
                                    </td>
                                    <td>

                                        <div className="form-group">
                                            <select
                                                className={alert ? "alertClass form-control" : "form-control  border border-surface-5"}
                                                value={productType}
                                                onChange={(e: any) => {
                                                    setProductType(e.target.value);
                                                    setAlert(false);
                                                    setIsApplySearch(true);
                                                }}
                                                disabled={systemGenerated}>
                                                <option value="">Select Product...</option>
                                                {enums?.["ProductType"].filter((p: any) => !p.value.includes("UNKNOWN"))?.map((curEnum: any) => {
                                                    return (
                                                        <option value={curEnum?.constant}>{curEnum?.value}</option>
                                                    )
                                                }
                                                )}
                                            </select>
                                        </div>
                                    </td>


                                    {utilData?.customerLevel === 'SYS' && (
                                        <td>
                                            <div className="form-group">
                                                <select
                                                    className="form-control border border-surface-5"
                                                    value={tenant}
                                                    onChange={(e) => {
                                                        setTenant(e.target.value);
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Any</option>
                                                    {tenantsQueryApi.data?.map((curTenant: any) => (
                                                        <option key={curTenant.name} value={curTenant.name}>
                                                            {curTenant.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                    )}

                                    <td>
                                        <div className="form-group">
                                            <select
                                                className={alert ? "alertClass form-control" : "form-control  border border-surface-5"}
                                                value={customer}
                                                onChange={(e: any) => {
                                                    setCustomer(e.target.value);
                                                    setAlert(false);
                                                    setIsApplySearch(true);
                                                }}
                                                disabled={systemGenerated}>
                                                <option value="">Any</option>
                                                {customerTenantResponseApi?.data && customerTenantResponseApi?.data?.map((curTenant: any) => {
                                                    return (
                                                        <>
                                                            <option
                                                                value={curTenant.id}>{curTenant.customerName}</option>
                                                        </>
                                                    )
                                                })}
                                            </select>
                                        </div>
                                    </td>

                                    {customer === '' ?
                                        <td>
                                            <div className="form-group">
                                                <select className="form-control" value={branch}
                                                    onChange={(e) => {
                                                        setBranch(e.target.value);
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Any</option>
                                                </select>
                                            </div>
                                        </td>
                                        :
                                        <td>
                                            <div className="form-group">
                                                <select className="form-control" value={branch}
                                                    onChange={(e) => {
                                                        setBranch(e.target.value);
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}>
                                                    <option value="">Any</option>
                                                    {branchData?.data && branchData?.data?.map((curBranch: any) => {
                                                        return (
                                                            <>
                                                                <option
                                                                    value={curBranch.id}>{curBranch.name}</option>
                                                            </>
                                                        )
                                                    })}
                                                </select>
                                            </div>
                                        </td>
                                    }
                                    <td>
                                        <div className="form-group">
                                            <select className="form-control  border border-surface-5" value={state}
                                                onChange={(e: any) => {
                                                    setState(e.target.value)
                                                    setCounty('');
                                                    setMetroArea('');
                                                    setIsApplySearch(true);
                                                }}
                                                disabled={systemGenerated}
                                            >
                                                <option value="">Any</option>
                                                {statesQueryApi.data && statesQueryApi.data.error === undefined && statesQueryApi?.data?.map((curState: any) => {
                                                    return (
                                                        <>
                                                            <option
                                                                value={curState.id}>{curState.stateName}</option>
                                                        </>
                                                    )
                                                })}
                                            </select>
                                        </div>
                                    </td>

                                    {state === '' ?
                                        <td>
                                            <div className="form-group">
                                                <select className="form-control  border border-surface-5" value={metroArea}
                                                    onChange={(e: any) => {
                                                        setMetroArea(e.target.value)
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Any</option>
                                                </select>
                                            </div>
                                        </td>

                                        :
                                        <td>
                                            <div className="form-group">
                                                <select className="form-control  border border-surface-5" value={metroArea}
                                                    onChange={(e) => {
                                                        setMetroArea(e.target.value);
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Any</option>
                                                    {state !== '' && metroAreaByStateId.data && metroAreaByStateId.data?.map((curMetroArea: any) => {
                                                        return (
                                                            <>
                                                                <option value={curMetroArea.id}>{curMetroArea.name}</option>
                                                            </>
                                                        )
                                                    })}
                                                </select>
                                            </div>
                                        </td>
                                    }
                                    {state === '' ?

                                        <td>
                                            <div className="form-group">
                                                <select className="form-control  border border-surface-5" value={county}
                                                    onChange={(e) => {
                                                        setCounty(e.target.value);
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Any</option>
                                                </select>
                                            </div>
                                        </td>
                                        :
                                        <td>
                                            <div className="form-group">
                                                <select className="form-control  border border-surface-5" value={county}
                                                    onChange={(e) => {
                                                        setCounty(e.target.value);
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Any</option>
                                                    {state !== '' && countyByStateId.data && countyByStateId.data?.map((curCounty: any) => {
                                                        return (
                                                            <>
                                                                <option
                                                                    value={curCounty.id}>{curCounty.countyName}</option>

                                                            </>
                                                        )
                                                    })}
                                                </select>
                                            </div>
                                        </td>
                                    }

                                    <td>
                                        <NumberFormat placeholder='0.00'
                                            className="form-control text-center border border-surface-5"
                                            decimalScale={2}
                                            decimalSeparator="."
                                            fixedDecimalScale
                                            prefix={"$"}
                                            value={displayPrice}
                                            thousandSeparator={true}
                                            onChange={(e: any) => {
                                                setDisplayPrice(e.target.value.replace('$', '').replaceAll(',', ''))
                                            }}
                                        />

                                    </td>
                                    <td>
                                        <NumberFormat placeholder='0.00'
                                            className="form-control text-center border border-surface-5"
                                            decimalScale={2}
                                            decimalSeparator="."
                                            fixedDecimalScale
                                            prefix={"$"}
                                            value={displayPricePerParcel}
                                            thousandSeparator={true}
                                            onChange={(e: any) => {
                                                setDisplayPricePerParcel(e.target.value.replace('$', '').replaceAll(',', ''))
                                            }}
                                        />

                                    </td>
                                    <td>
                                        <NumberFormat placeholder='0.00'
                                            className="form-control text-center border border-surface-5"
                                            decimalScale={2}
                                            decimalSeparator="."
                                            fixedDecimalScale
                                            prefix={"$"}
                                            value={price}
                                            thousandSeparator={true}
                                            onChange={(e: any) => {
                                                setPrice(e.target.value.replace('$', '').replaceAll(',', ''))
                                            }}
                                        />

                                    </td>
                                    <td>
                                        <NumberFormat placeholder='0.00'
                                            className="form-control text-center border border-surface-5"
                                            decimalScale={2}
                                            decimalSeparator="."
                                            fixedDecimalScale
                                            prefix={"$"}
                                            value={pricePerParcel}
                                            thousandSeparator={true}
                                            onChange={(e: any) => {
                                                setPricePerParcel(e.target.value.replace('$', '').replaceAll(',', ''))
                                            }}
                                        />

                                    </td>
                                    <td>
                                        <i className={!showEditIcon ? 'fa ml-3 mt-2 element icon fa-plus-circle cursor_hover text-highlight-0' : 'fa ml-3 mt-2 element icon fa-edit cursor_hover text-highlight-0'}
                                            title="Add Price ..." onClick={handleSubmit} />
                                    </td>
                                </tr>}

                                {customerPricingData.data && customerPricingData.data.data !== undefined && customerPricingData.data.data.length === 0 &&
                                    <tr>
                                        <td colSpan={9}>There are No customer pricing to display</td>
                                    </tr>
                                }
                                {customerPricingData.data && customerPricingData.data.error === undefined ? customerPricingData.data.data !== undefined && customerPricingData.data.data?.map((curPricing: any, index: any) => {
                                    return (
                                        <tr key={index} className={"odd:bg-surface-7 hover:bg-surface-7"}>

                                            <td>

                                                {hasPermission(["PRIC-U"]) ?
                                                    <a className="text-highlight-4 active cursor-pointer" onClick={(e) => {
                                                        setShowEditIcon(true);
                                                        setAlert(false);
                                                        showEditRow(curPricing);
                                                        setIsApplySearch(true);
                                                    }}>{curPricing.description}</a> : <>{curPricing.description}</>
                                                }

                                            </td>

                                            <td className="text-center">
                                                {enums?.["ProductType"]?.find((curEnum: any) => curEnum.constant === curPricing?.productType)?.value || 'Any'}
                                            </td>

                                            {utilData?.customerLevel === 'SYS' && (
                                                <td>{curPricing?.tenant || 'Any'}</td>
                                            )}
                                            <td>{curPricing?.customerName || 'Any'}</td>
                                            <td>{curPricing?.branchName || 'Any'}</td>
                                            <td>{curPricing?.stateName || 'Any'}</td>
                                            <td>{curPricing?.metroAreaName || 'Any'}</td>
                                            <td>{curPricing?.countyName || 'Any'}</td>
                                            <td>
                                                <NumberFormat placeholder='0.00'
                                                    className="form-control text-center border border-surface-5"
                                                    decimalScale={2}
                                                    decimalSeparator="."
                                                    fixedDecimalScale
                                                    prefix={"$"}
                                                    thousandSeparator={true}
                                                    value={curPricing.displayPrice}
                                                    disabled={true}
                                                />
                                            </td>
                                            <td>
                                                <NumberFormat placeholder='0.00'
                                                    className="form-control text-center border border-surface-5"
                                                    decimalScale={2}
                                                    decimalSeparator="."
                                                    fixedDecimalScale
                                                    prefix={"$"}
                                                    thousandSeparator={true}
                                                    value={curPricing.displayPricePerParcel}
                                                    disabled={true}
                                                /></td>
                                            <td>
                                                <NumberFormat placeholder='0.00'
                                                    className="form-control text-center border border-surface-5"
                                                    decimalScale={2}
                                                    decimalSeparator="."
                                                    fixedDecimalScale
                                                    prefix={"$"}
                                                    thousandSeparator={true}
                                                    value={curPricing.price}
                                                    disabled={true}
                                                /></td>
                                            <td>
                                                <NumberFormat placeholder='0.00'
                                                    className="form-control text-center border border-surface-5"
                                                    decimalScale={2}
                                                    decimalSeparator="."
                                                    fixedDecimalScale
                                                    prefix={"$"}
                                                    thousandSeparator={true}
                                                    value={curPricing.pricePerParcel}
                                                    disabled={true}
                                                />
                                            </td>
                                            <td>
                                                {(curPricing?.systemGenerated === false) && hasPermission(["PRIC-D"]) &&


                                                    <ModalButton
                                                        triggerButton={
                                                            <IconButton
                                                                icon={<FaTrash />}
                                                                title={"Delete Customer Pricing"}
                                                                hasBackground={false}
                                                                className="mr-2 text-highlight-0"
                                                            />
                                                        }
                                                        modal={
                                                            <ConfirmationModal
                                                                show={true}
                                                                setShow={() => {
                                                                }}
                                                                title={"Delete Customer Pricing"}
                                                                bodyMessage={"Are you sure you want to delete this Customer Pricing?"}
                                                                onSubmit={() => handleDelete(curPricing.id)}
                                                                isLoading={isLoading}
                                                            />
                                                        }
                                                    />

                                                }
                                            </td>
                                        </tr>
                                    )
                                }) :
                                    "no data found"
                                }

                            </tbody>
                        </table>

                    </div>
                    {
                        (customerPricingData?.data && customerPricingData?.data?.totalPages != null && customerPricingData?.data?.totalPages > 0) ?
                            <Pagination
                                displayText={customerPricingData?.data && customerPricingData?.data?.displayingText}
                                pageCount={customerPricingData?.data && customerPricingData?.data?.totalPages}
                                setPageNumber={setPageNumber}
                                pageNumber={pageNumber}
                                groupName="Customer-Pricing"
                                setExternalPageSize={setPageSize}
                                permissions={"-"}

                            />
                            : ''
                    }
                </>
            }
        </>

    )
}
export default CustomerPricing;